import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Truck, Mail } from "lucide-react";
import { api, imageUrl, formatPrice } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, items } = useCart();
  const { payments_enabled } = useSettings();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((res) => {
        setProduct(res.data);
        setActiveIdx(0);
      })
      .catch(() => setError("Artwork not found"))
      .finally(() => setLoading(false));
  }, [id]);

  // Build the image list — prefer image_paths array, fall back to legacy single image_path.
  const images = useMemo(() => {
    if (!product) return [];
    const list = (product.image_paths || []).filter(Boolean);
    if (list.length > 0) return list;
    return product.image_path ? [product.image_path] : [];
  }, [product]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center label-eyebrow text-[#5B5653]">
        Loading…
      </div>
    );
  }
  if (error || !product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6">
        <p className="label-eyebrow">Not Found</p>
        <Link to="/" className="btn-secondary">
          Back to Store
        </Link>
      </div>
    );
  }

  const inCart = items.find((i) => i.id === product.id);
  const sold = product.status === "sold";

  const handleAdd = () => {
    addItem(product);
    toast.success("Added to cart", { description: product.title });
  };

  return (
    <div data-testid="product-detail" className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <button
        data-testid="back-btn"
        onClick={() => navigate(-1)}
        className="label-eyebrow text-[#5B5653] hover:text-[#E25C46] flex items-center gap-2 mb-10"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20">
        <div className="md:col-span-7">
          <div
            data-testid="product-image-main"
            className="bg-[#EFEBE6] overflow-hidden"
          >
            <img
              key={images[activeIdx] || "empty"}
              src={imageUrl(images[activeIdx])}
              alt={product.title}
              className="w-full h-auto max-h-[80vh] md:max-h-none object-contain fade-up"
            />
          </div>
          {images.length > 1 && (
            <div
              data-testid="product-thumbnails"
              className="mt-4 grid grid-cols-5 gap-3"
            >
              {images.map((path, idx) => (
                <button
                  key={path + idx}
                  type="button"
                  data-testid={`product-thumbnail-${idx}`}
                  onClick={() => setActiveIdx(idx)}
                  aria-label={`Show photo ${idx + 1}`}
                  className={`aspect-[4/5] overflow-hidden bg-[#EFEBE6] transition-all ${
                    idx === activeIdx
                      ? "ring-2 ring-[#E25C46] ring-offset-2 ring-offset-[#F8F5F2]"
                      : "opacity-80 hover:opacity-100"
                  }`}
                >
                  <img
                    src={imageUrl(path)}
                    alt={`${product.title} photo ${idx + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-5 flex flex-col">
          <p className="label-eyebrow">Original Painting</p>
          <h1
            data-testid="product-title"
            className="font-serif text-4xl md:text-5xl font-light text-[#2D2926] mt-3 tracking-tight leading-tight"
          >
            {product.title}
          </h1>

          <div className="mt-8 space-y-4 text-sm">
            <div className="flex justify-between border-b border-[#E3DDD7] pb-3">
              <span className="label-eyebrow">Size</span>
              <span data-testid="product-size" className="text-[#2D2926]">
                {product.size}
              </span>
            </div>
            <div className="flex justify-between border-b border-[#E3DDD7] pb-3">
              <span className="label-eyebrow">Price</span>
              <span data-testid="product-price" className="text-[#2D2926] font-serif text-xl">
                {formatPrice(product.price)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[#3E6649] pt-2">
              <Truck className="w-4 h-4" />
              <span className="text-sm">Free delivery anywhere in Australia</span>
            </div>
          </div>

          <div className="mt-10">
            <p className="label-eyebrow mb-3">The Story</p>
            <p
              data-testid="product-story"
              className="text-[#5B5653] leading-relaxed whitespace-pre-wrap"
            >
              {product.story}
            </p>
            <div
              data-testid="product-copyright-notice"
              className="mt-6 pl-4 border-l-2 border-[#E25C46]/60 text-xs text-[#5B5653] leading-relaxed"
            >
              <p>
                © {new Date().getFullYear()} Colleen Prosser. This artwork, the photograph of
                it, and the story above are the copyright and cultural property of the artist.
                Purchase of the original does not transfer reproduction rights.{" "}
                <Link
                  to="/copyright"
                  data-testid="product-copyright-link"
                  className="underline underline-offset-4 hover:text-[#E25C46]"
                >
                  Read full notice
                </Link>
                .
              </p>
            </div>
          </div>

          <div className="mt-12 flex gap-3">
            {sold ? (
              <button data-testid="sold-btn" className="btn-secondary w-full" disabled>
                Sold
              </button>
            ) : !payments_enabled ? (
              <a
                href={`mailto:colleen.prosser@outlook.com?subject=${encodeURIComponent(
                  `Enquiry — ${product.title}`
                )}&body=${encodeURIComponent(
                  `Hi Colleen,\n\nI'd like to enquire about purchasing "${product.title}" (${product.size}, ${formatPrice(product.price)}).\n\nThanks,\n`
                )}`}
                data-testid="enquire-btn"
                className="btn-primary flex-1"
              >
                <Mail className="w-4 h-4 mr-2" /> Enquire to Purchase
              </a>
            ) : inCart ? (
              <Link to="/checkout" data-testid="go-checkout-btn" className="btn-primary flex-1">
                Go to Checkout
              </Link>
            ) : (
              <button
                data-testid="add-to-cart-btn"
                onClick={handleAdd}
                className="btn-primary flex-1"
              >
                Add to Cart
              </button>
            )}
          </div>
          {!payments_enabled && !sold && (
            <p className="mt-4 text-xs text-[#5B5653]">
              Online checkout is being set up. To purchase this piece now, please email Colleen
              directly — she'll arrange a secure payment and free delivery.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
